import PropTypes from 'prop-types';
import { getVerifiedLocales, useLocale, useStoreActions } from '@app/store';
import { Languages } from '@constants';
import React, { useState, useCallback, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@components/popover';
import { cn } from '@utils/general';
import LanguageIcon from '@components/icons/LanguageIcon';

const LocaleSelector = ({
  ctaColor, triggerClassName, popoverClassName, iconSize,
}) => {
  const locale = useLocale();
  const { setLocaleAction } = useStoreActions();
  const verifiedLocales = getVerifiedLocales();

  const [openLanguageModal, setOpenLanguageModal] = useState(false);

  const popoverStyle = ctaColor ? {
    zIndex: 'var(--z-sdk-popover)',
    '--cta-color': ctaColor,
  } : { zIndex: 'var(--z-sdk-popover)' };

  const handleLanguageChange = useCallback((loc) => {
    setLocaleAction(loc);
    setOpenLanguageModal(false);
  }, [setLocaleAction]);

  const allLanguages = useMemo(() => {
    const codes = Object.keys(Languages);
    if (!verifiedLocales || verifiedLocales.length === 0) {
      return codes.map((code) => Languages[code]);
    }
    return codes
      .filter((code) => verifiedLocales.includes(code))
      .map((code) => Languages[code]);
  }, [verifiedLocales]);

  const handleLanguageClick = useCallback(
    (localeCode) => () => handleLanguageChange(localeCode),
    [handleLanguageChange],
  );

  const handleLanguageKeyDown = useCallback((localeCode) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleLanguageChange(localeCode);
    }
  }, [handleLanguageChange]);

  const toggleLanguageModal = useCallback(() => {
    setOpenLanguageModal((prev) => !prev);
  }, []);

  return (
    <Popover open={openLanguageModal} onOpenChange={setOpenLanguageModal}>
      <PopoverTrigger
        onClick={toggleLanguageModal}
        style={ctaColor ? { '--icon-color': ctaColor } : {}}
        className={cn('cursor-pointer mt-1', triggerClassName, { 'cta-icon-trigger': ctaColor })}
      >
        <LanguageIcon size={iconSize} />
      </PopoverTrigger>
      <PopoverContent style={popoverStyle}>
        <div className={cn('locale-selector-popover flex flex-col gap-2 p-4 text-sm text-gray-600 bg-white border rounded min-w-48 max-h-96 overflow-auto', popoverClassName)}>
          {allLanguages.map((lang) => (
            <div
              className={cn('cursor-pointer focus:outline-none', {
                'font-bold text-primary-700': lang.locale === locale,
              }, ctaColor ? 'cta-styled-text' : '', ctaColor && lang.locale === locale ? 'selected' : '')}
              key={lang.locale}
              onClick={handleLanguageClick(lang.locale)}
              onKeyDown={handleLanguageKeyDown(lang.locale)}
              tabIndex={0}
              role="button"
              aria-label={lang.label}
            >
              {lang.label}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

LocaleSelector.defaultProps = {
  ctaColor: null,
  triggerClassName: '',
  popoverClassName: '',
  iconSize: 25,
};

LocaleSelector.propTypes = {
  ctaColor: PropTypes.string,
  // Extra classes for the icon trigger (e.g. a host's icon-button hit area).
  triggerClassName: PropTypes.string,
  // Extra classes for the popover list (e.g. a page-scoped class, since it renders in a portal).
  popoverClassName: PropTypes.string,
  // Language icon size in px (e.g. the consent notice's header iconProps.size).
  iconSize: PropTypes.number,
};

export default LocaleSelector;
